import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ScrollToTop from "@/components/feature/ScrollToTop";
import BackToTop from "@/components/feature/BackToTop";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import GossipCard from "@/components/base/GossipCard";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { gossipList } from "@/mocks/gossip";
import { categories } from "@/mocks/site";

const verdicts = ["全部", "实锤", "存疑", "待定", "辟谣"];
const sorts = [
  { key: "credibility", label: "可信度从高到低" },
  { key: "heat", label: "热度从高到低" },
  { key: "date", label: "最新发布优先" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: "爆料库 · 瓜田爆料",
      url: `${SITE_ORIGIN}/gossip`,
      description:
        "瓜田爆料库收录已完成真假鉴定与可信度评估的热门爆料，可按类别、结论标签与可信度筛选。",
      inLanguage: "zh-CN",
      isPartOf: { "@type": "WebSite", url: SITE_ORIGIN, name: "瓜田爆料" },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "爆料库", item: `${SITE_ORIGIN}/gossip` },
      ],
    },
    {
      "@type": "ItemList",
      name: "已鉴定爆料条目",
      numberOfItems: gossipList.length,
      itemListElement: gossipList.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `${SITE_ORIGIN}/gossip/${item.id}`,
      })),
    },
  ],
};

export default function GossipLibrary() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const cat = params.get("cat") ?? "全部";
  const verdict = params.get("verdict") ?? "全部";
  const sort = params.get("sort") ?? "credibility";

  useSeo({
    title: "爆料库 | 真假鉴定与可信度评分 · 瓜田爆料",
    description:
      "浏览瓜田爆料库：按恋情绯闻、事业动向、口碑争议等类别筛选已鉴定条目，查看每条爆料的证据链、结论标签与 0-100 可信度评分。",
    keywords: ["爆料库", "真假鉴定", "可信度评分", "证据链", "实锤分析", "辟谣汇总"],
    path: "/gossip",
    ogType: "website",
    jsonLd,
  });

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const results = useMemo(() => {
    let list = [...gossipList];
    if (cat !== "全部") list = list.filter((i) => i.category === cat);
    if (verdict !== "全部") list = list.filter((i) => i.verdict === verdict);
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.title.toLowerCase().includes(kw) ||
          i.summary.toLowerCase().includes(kw) ||
          i.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    if (sort === "heat") list.sort((a, b) => b.heat - a.heat);
    else if (sort === "date") list.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
    else list.sort((a, b) => b.credibility - a.credibility);
    return list;
  }, [cat, verdict, q, sort]);

  const hasFilter = cat !== "全部" || verdict !== "全部" || q.trim() !== "";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <ScrollToTop />
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100 paper-texture">
          <div className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "爆料库" }]} />
            <h1 className="mt-5 font-heading text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              爆料库
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600 leading-relaxed">
              收录已完成真假鉴定的爆料条目。每条都附带证据链、结论标签与可信度分数，
              你可以按结论或类别筛选，先看最需要核实的那些。
            </p>
            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-xs text-foreground-500">
              <span>共 <strong className="font-mono-num text-foreground-900">{gossipList.length}</strong> 条已鉴定</span>
              <span>其中实锤 <strong className="font-mono-num text-primary-600">{gossipList.filter((i) => i.verdict === "实锤").length}</strong> 条</span>
              <span>辟谣 <strong className="font-mono-num text-secondary-700">{gossipList.filter((i) => i.verdict === "辟谣").length}</strong> 条</span>
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28 space-y-6">
                <div>
                  <label htmlFor="gossip-search" className="block text-sm font-medium text-foreground-900 mb-2">
                    关键词搜索
                  </label>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                    <input
                      id="gossip-search"
                      type="search"
                      value={q}
                      onChange={(e) => update("q", e.target.value)}
                      placeholder="输入关键词或标签"
                      className="w-full pl-9 pr-3 py-2.5 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                    />
                  </div>
                </div>

                <div>
                  <h2 className="text-sm font-medium text-foreground-900 mb-2">主题分类</h2>
                  <ul className="space-y-1">
                    {["全部", ...categories.map((c) => c.key)].map((key) => {
                      const active = cat === key;
                      const count =
                        key === "全部"
                          ? gossipList.length
                          : gossipList.filter((i) => i.category === key).length;
                      return (
                        <li key={key}>
                          <button
                            type="button"
                            onClick={() => update("cat", key)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-control text-sm transition-colors ${
                              active
                                ? "bg-primary-50 text-primary-700 font-medium"
                                : "text-foreground-600 hover:bg-background-100"
                            }`}
                          >
                            <span>{key}</span>
                            <span className="font-mono-num text-[11px] text-foreground-400">{count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div>
                  <h2 className="text-sm font-medium text-foreground-900 mb-2">结论标签</h2>
                  <div className="flex flex-wrap gap-2">
                    {verdicts.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => update("verdict", v)}
                        className={`px-3 py-1.5 rounded-control text-xs border transition-colors whitespace-nowrap ${
                          verdict === v
                            ? "bg-foreground-950 text-background-50 border-foreground-950"
                            : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="gossip-sort" className="block text-sm font-medium text-foreground-900 mb-2">
                    排序方式
                  </label>
                  <select
                    id="gossip-sort"
                    value={sort}
                    onChange={(e) => update("sort", e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-control border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                  >
                    {sorts.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {hasFilter && (
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="w-full px-3 py-2.5 rounded-control border border-background-300 text-sm text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-colors"
                  >
                    清空全部筛选
                  </button>
                )}
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-background-200">
                <p className="text-sm text-foreground-600">
                  找到 <strong className="font-mono-num text-foreground-950">{results.length}</strong> 条条目
                  {cat !== "全部" && <span className="text-foreground-400"> · {cat}</span>}
                  {verdict !== "全部" && <span className="text-foreground-400"> · {verdict}</span>}
                </p>
                <p className="text-xs text-foreground-400">点击卡片可查看完整证据链与时间线</p>
              </div>

              {results.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {results.map((item) => (
                    <GossipCard key={item.id} item={item} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 border border-dashed border-background-300 rounded-card p-12 text-center">
                  <i className="ri-search-eye-line text-3xl text-foreground-300"></i>
                  <p className="mt-4 text-sm text-foreground-600">没有匹配的条目，换个关键词或清空筛选试试。</p>
                  <button
                    type="button"
                    onClick={() => setParams(new URLSearchParams(), { replace: true })}
                    className="mt-4 px-5 py-2.5 rounded-control bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
                  >
                    清空筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}